<script lang="ts">
  import { onMount } from 'svelte';
  import { navigate } from 'svelte-navigator';
  import { _ } from 'svelte-i18n';
  import InfoIcon from '../../../assets/icons/info.svg.svelte';

  // @ts-ignore - Vite/webpack will resolve asset imports to URL strings
  import mbdParticipation from '../../../assets/MBOS Cashback-Aktion 2026 -Teilnahmebedingungen_v1.0.pdf';
  // @ts-ignore - Vite/webpack will resolve asset imports to URL strings

  import { handleFetchRetry } from '../../handleFetchRetry';
  import { debugLog } from '../../../lib/debug-log';
  import { clearStoredReferer } from '../../../utils/utmTracking';

  let purchasePaybackId: string;
  // backup endpoint removed in new flow
  let purchaseReceiptsBackupId: string | undefined;

  // Get backup receipt ID from recognizedText localStorage
  if (localStorage.getItem('recognizedText') !== null) {
    const recognizedText = JSON.parse(
      localStorage.getItem('recognizedText') as string,
    );
    purchaseReceiptsBackupId = recognizedText?.backupReceiptId;
  }
  let showThankYouMessage = false;
  let showMissingPersonalInfoModal = false;
  let termsAccepted = false;
  let isSubmitting = false;
  let isAlreadySubmitted = false;

  let URL = `${process.env.SVELTE_APP_FE_REMOTE_URL}/api/mbdmbos`;

  // read from localstorage
  if (localStorage.getItem('purchasePayBack') !== null) {
    purchasePaybackId = JSON.parse(
      localStorage.getItem('purchasePayBack') as string,
    ).id;
  }

  // Check if this ID has already been submitted
  const getCompletedIds = (): string[] => {
    const stored = localStorage.getItem('completedPurchasePaybackIds');
    return stored ? JSON.parse(stored) : [];
  };
  const isIdCompleted = (id: string): boolean => {
    const completedIds = getCompletedIds();
    return completedIds.includes(id);
  };
  const markIdAsCompleted = (id: string): void => {
    const completedIds = getCompletedIds();
    if (!completedIds.includes(id)) {
      completedIds.push(id);
      localStorage.setItem(
        'completedPurchasePaybackIds',
        JSON.stringify(completedIds),
      );
    }
  };

  // Not available in new response; keep undefined

  $: {
    debugLog({ purchaseReceiptsBackupId });
  }

  const checkPersonalInformationSaved = (): boolean => {
    const stored = localStorage.getItem('personalInformation');
    if (!stored) return false;
    try {
      const parsed = JSON.parse(stored);
      return !!(parsed && parsed.id && parsed.email);
    } catch (e) {
      return false;
    }
  };

  const handleMissingPersonalInfoClose = () => {
    showMissingPersonalInfoModal = false;
    navigate('/step/3' + window.location.search);
  };

  onMount(() => {
    const alreadyCompleted = purchasePaybackId ? isIdCompleted(purchasePaybackId) : false;
    if (!alreadyCompleted && !showThankYouMessage && !checkPersonalInformationSaved()) {
      showMissingPersonalInfoModal = true;
    }
  });

  $: isFormValid = termsAccepted;

  // Check if ID is already submitted when purchasePaybackId changes
  $: if (purchasePaybackId) {
    isAlreadySubmitted = isIdCompleted(purchasePaybackId);
    if (isAlreadySubmitted) {
      clearStoredReferer();
    }
  }

  const isValidPurchaseDate = (dateString: string): boolean => {
    const ed = process.env.SVELTE_APP_APP_END_DATE?.split('-');
    const sd = process.env.SVELTE_APP_APP_START_DATE?.split('-');

    if (ed && sd) {
      const startDate = new Date(Date.UTC(+sd[0], +sd[1] - 1, +sd[2]));
      const endDate = new Date(Date.UTC(+ed[0], +ed[1] - 1, +ed[2]));
      const purchaseDate = new Date(dateString);

      if (purchaseDate > startDate && purchaseDate < endDate) return true;
    }
    return false;
  };

  const determineCashbackType = (receiptInformation: any): string => {
    if (!receiptInformation) return 'cashback-basic';
    
    // Use new structured fields first, fallback to old fields for compatibility
    const switchDetected = receiptInformation.switchDetected === '1' || 
                          !!receiptInformation.switch || 
                          !!receiptInformation.switchIdentified;
    
    const storageDetected = receiptInformation.storageDetected === '1' || 
                           !!receiptInformation.storage || 
                           !!receiptInformation.storageIdentified;
    
    // Business rules: switch is mandatory for basic, both switch + storage for full
    if (switchDetected && storageDetected) {
      return 'cashback-full';
    } else if (switchDetected) {
      return 'cashback-basic';
    } else {
      // No switch detected - still basic but will need manual verification
      return 'cashback-basic';
    }
  };

  const defineStatus = async () => {
    let receiptInformation = null;
    let purchasePaybackInformation = null;

    if (localStorage.getItem('receipt'))
      receiptInformation = JSON.parse(
        localStorage.getItem('receipt') as string,
      );
    if (localStorage.getItem('purchasePayBack'))
      purchasePaybackInformation = JSON.parse(
        localStorage.getItem('purchasePayBack') as string,
      );

    if (!!receiptInformation || !!purchasePaybackInformation)
      return 'VERIFICATION';

    if (
      receiptInformation.partsCount !== '4' ||
      JSON.parse(receiptInformation.partsId).length <= 0
    ) {
      return 'VERIFICATION';
    }

    const requests = [
      `${URL}/purchasereceipts/${purchasePaybackInformation.purchasereceiptsId}`,
      `${URL}/purchasepaybacks/${purchasePaybackInformation.id}`,
      `${URL}/dealerships/${receiptInformation.dealershipsId}`,
    ];

    let isValidDealership = null;
    let validPurchaseDate = null;
    let switchDetected = null;
    let noOverridedValues = null;

    await Promise.all(requests.map((r) => fetch(r)))
      .then(async (res) => {
        const purchaseReceiptsResult = await res[0].json();
        const purchasePaybacksResult = await res[1].json();

        const overrideFields = Object.keys(purchaseReceiptsResult).filter((k) =>
          k.includes('Override'),
        );

        for (let k = 0; k < overrideFields.length; k++) {
          if (purchaseReceiptsResult[overrideFields[k]]) {
            k = overrideFields.length;
          } else {
            if (k == overrideFields.length - 1) noOverridedValues = true;
          }
        }

        isValidDealership = res[2].status == 200;
        // Check if switch is detected (mandatory for any cashback)
        switchDetected = purchaseReceiptsResult.switchDetected === '1' || 
                        !!purchaseReceiptsResult.switch || 
                        !!purchaseReceiptsResult.switchIdentified;
        validPurchaseDate = isValidPurchaseDate(purchasePaybacksResult.date);
      })
      .catch((error) => debugLog({ error }));

    if (
      !isValidDealership ||
      !switchDetected ||
      !validPurchaseDate ||
      noOverridedValues
    )
      return 'VERIFICATION';

    return 'PRE-APPROVED';
  };

  const updatePurchasePayback = async () => {
    if (!checkPersonalInformationSaved()) {
      debugLog('Personal information is not saved');
      isSubmitting = false;
      showMissingPersonalInfoModal = true;
      return;
    }

     // Prevent duplicate submission for the same ID
     if (purchasePaybackId && isIdCompleted(purchasePaybackId)) {
      debugLog('This ID has already been submitted');
      isSubmitting = false;
      return;
    }
    // umami.track(props => ({ ...props, url: '/step/6', title: 'Submitted' }))
    // const item = localStorage.getItem("customerProcess");
    // const customerProcess = (item !== null) ? JSON.parse(item) : null;

    // const validationObj: IValidationObj = {
    //   personalInformation: true,
    //   registration: true,
    //   questions: true,
    // };
    // if(customerProcess == null) {
    //   status = "VERIFICATION";
    // } else {
    //   const validationKeys = Object.keys(validationObj);

    //   validationKeys.map(k => {
    //     if(customerProcess[k] !== validationObj[k]) {
    //       status = "VERIFICATION";
    //     };
    //   });
    // };
    try {
      let receiptInformation: any = null;

      if (localStorage.getItem('receipt'))
        receiptInformation = JSON.parse(
          localStorage.getItem('receipt') as string,
        );

      const response = await handleFetchRetry(async () =>
        fetch(`${URL}/purchasepaybacks/${purchasePaybackId}`, {
          method: 'PATCH',
          headers: {
            Accept: 'application/json, text/plain',
            'Content-Type': 'application/json;charset=UTF-8',
            'X-Custom-Header': 'mb-cashback-2025-header',
          },
          body: JSON.stringify({
            completed: 1,
            status: await defineStatus(),
            cashbackType: determineCashbackType(receiptInformation),
          }),
        }),
      );


      debugLog('response', response);

      // Update backup purchase receipt if backup ID exists
      if (purchaseReceiptsBackupId && receiptInformation) {
        await handleFetchRetry(async () =>
          fetch(`${URL}/purchasereceipts-backups/${purchaseReceiptsBackupId}`, {
            method: "PATCH",
            headers: {
              Accept: "application/json, text/plain",
              "Content-Type": "application/json;charset=UTF-8",
              'X-Custom-Header': 'mb-cashback-2025-header',
            },
            body: JSON.stringify({
              cashbackType: determineCashbackType(receiptInformation),
            }),
          })
        );
      }

      if (response.status === 200 || response.status === 204) {
        // read personalInformation from localstorage
        const personalInformation = JSON.parse(
          localStorage.getItem('personalInformation') as string,
        );

        const purchasepaybackID = JSON.parse(
          localStorage.getItem('purchasePayBack') as string,
        ).id;
        const purchasereceiptsID = JSON.parse(
          localStorage.getItem('receipt') as string,
        ).id;
        const isProd = process.env.SVELTE_APP_GRAPPLE_STAGE === 'prd';
        const emailPayload = {
          email: personalInformation.email,
          name: personalInformation.name,
          onlyRecipient: !isProd,
          processIDs: {
            purchasepaybackID: purchasepaybackID || '',
            purchasereceiptsID: purchasereceiptsID || '',
            personalinformationsID: personalInformation?.id || '',
          },
        };
        // send email
        await fetch(
          'https://mkmubkildtb4uk6w47xzr4ceum0rkyag.lambda-url.eu-central-1.on.aws/send-email',
          {
            method: 'POST',
            headers: {
              Accept: 'application/json, text/plain',
              'Content-Type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(emailPayload),
          },
        );
        // Mark this ID as completed to prevent duplicate submissions
        markIdAsCompleted(purchasePaybackId);
        isAlreadySubmitted = true;
        showThankYouMessage = true;

        // Clean up process keys from localStorage upon completion
        localStorage.removeItem('personalInformation');
        localStorage.removeItem('questionsResponses');
        localStorage.removeItem('customerProcess');
        localStorage.removeItem('recognizedText');
        localStorage.removeItem('receipt');
        localStorage.removeItem('reciept');
        localStorage.removeItem('purchasePayBack');
        // Remove referer at the very end of the process
        clearStoredReferer();
      } else {
        debugLog('error saving purchasePayback request');
        isSubmitting = false;
      }
    } catch (error) {
      debugLog('error', error);
      isSubmitting = false;
    }
  };
</script>

<div class="flex flex-1 flex-col space-y-6">
  <div>
    <h1 class="font-CorpoA text-3xl text-black">
      {$_('Submit Application')}
    </h1>
  </div>

  <!-- Introduction Text -->
  <div class="text-sm leading-relaxed text-black">
    <p class="mb-4">
      {$_('Cashback intro text')}
    </p>
  </div>

  <!-- Terms and Conditions Section -->
  <div class="mb-8">
    <div class="mb-6 flex items-start gap-4">
      <input
        type="checkbox"
        id="terms-checkbox"
        class="mt-1 h-6 w-6 rounded text-primary-600 focus:ring-2 focus:ring-primary-500"
        bind:checked={termsAccepted}
        data-umami-event="terms checkbox clicked"
        data-umami-event-page="/step/5"
      />
      <div class="flex-1">
        <label
          for="terms-checkbox"
          class="text-sm leading-relaxed text-gray-700"
        >
          <span>{$_('I accept the')} </span>
          <a
            href={mbdParticipation}
            download
            class="font-medium text-primary-600 underline hover:text-primary-800"
          >
            {$_('Terms and Conditions')}
          </a>
          <span class="ml-1 text-cubiq-dark-blue">*</span>
        </label>
        <div class="mt-2 text-xs text-black">
          <p>
            {$_('Privacy notice text')}
          </p>
        </div>
      </div>
    </div>
  </div>



  <!-- Required Field Notice -->
  <div class="mb-8 text-xs text-black">
    <span class="text-cubiq-dark-blue">*</span>
    {$_('required')}
  </div>

  <!-- Instructions -->
  <div class="mb-8 space-y-3 text-sm text-black">
    <div class="flex items-start gap-3">
      <InfoIcon class="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
      <p>
        {$_('Submit application info 1')}
      </p>
    </div>
    <div class="flex items-start gap-3">
      <InfoIcon class="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-500" />
      <p>
        {$_('Submit application info 2')}
      </p>
    </div>
  </div>

  <!-- Submit Button -->
  <div class="flex justify-end">
    <button
      class="rounded bg-primary-600 px-8 py-3 font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:bg-gray-400"
      disabled={!isFormValid || isSubmitting || isAlreadySubmitted}
      data-umami-event="submit button clicked"
      data-umami-event-page="/step/5"
      on:click={async () => {
        isSubmitting = true;
        await updatePurchasePayback();
      }}
    >
      {$_('Submit')} →
    </button>
  </div>

  <!-- Thank You Modal -->
  {#if showThankYouMessage}
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div class="mx-4 max-w-md rounded-lg bg-white p-8 text-center">
        <div class="mb-6">
          <div
            class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100"
          >
            <svg
              class="h-8 w-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h3 class="mb-2 text-xl font-semibold text-black">
            {$_('Thank you for your participation!')}
          </h3>
          <p class="text-black">
            {$_(
              'We will inform you via email about the status of your application',
            )}
          </p>
        </div>
        <p class="text-sm text-black">
          {$_('Close window')}
        </p>
      </div>
    </div>
  {/if}

  <!-- Missing Personal Information Modal -->
  {#if showMissingPersonalInfoModal}
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      aria-labelledby="missing-personal-info-title"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      on:click|self={handleMissingPersonalInfoClose}
      on:keydown={(e) => {
        if (e.key === 'Escape') handleMissingPersonalInfoClose();
      }}
    >
      <div class="relative mx-4 max-w-md rounded-lg bg-white p-8 text-center shadow-xl">
        <button
          type="button"
          class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none"
          aria-label={$_('Close')}
          on:click={handleMissingPersonalInfoClose}
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div class="mb-6">
          <div
            class="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 text-amber-600"
          >
            <svg
              class="h-8 w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3
            id="missing-personal-info-title"
            class="mb-2 text-xl font-semibold text-black"
          >
            {$_('Missing Personal Information')}
          </h3>
          <p class="text-sm text-gray-600">
            {$_(
              'Please provide and save your personal information before completing your application.',
            )}
          </p>
        </div>
        <button
          type="button"
          class="w-full rounded bg-primary-600 px-6 py-3 font-medium text-white transition-colors hover:bg-primary-700"
          on:click={handleMissingPersonalInfoClose}
        >
          {$_('To Personal Information')}
        </button>
        <button
          type="button"
          class="mt-4 w-full cursor-pointer text-sm text-gray-500 hover:text-black focus:outline-none"
          on:click={handleMissingPersonalInfoClose}
        >
          {$_('Close')}
        </button>
      </div>
    </div>
  {/if}
</div>